import * as React from "react";
import styled from "styled-components";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import pattern from "patternomaly";
import moment from "../../../utilities/Moment";
import NumberFormatter from "../../../utilities/NumberFormatter";
import { Style } from "../../../styles";
import GraphEmpty from "../../Graph/GraphEmpty";
import { DashboardController } from "../../../controllers";
import PageLoader from "../../Page/PageLoader";

const GraphContainer = styled.div`
  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    overflow-x: auto;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const GraphWrapper = styled.div`
  position: relative;
  height: 100%;

  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    height: 300px;
    width: 1400px;
  }
`;

interface MonthlySummary {
  revenue: number;
  expected_revenue: number;
  expenses: number;
  expected_expenses: number;
  net_income: number;
  expected_net_income: number;
  month: string;
}

interface IProps {
  currency: string;
  numberFormat: string;
}

const FinancialPerformanceWidget = (props: IProps) => {
  const { currency, numberFormat } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [monthlySummaries, setMonthlySummaries] = React.useState<MonthlySummary[]>([]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await DashboardController.getMonthlySummaries();
      setMonthlySummaries(response);
    } catch (error) {
      console.error("Failed to fetch monthly summaries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="panel h-full flex flex-col mb-0!">
        <div className="panel-header is-large">{t("Dashboard::Financial performance")}</div>
        <div className="panel-content is-large min-h-0 flex-1 overflow-auto">
          <PageLoader />
        </div>
      </div>
    );
  }

  const labels = monthlySummaries.map((summary) => summary.month);

  const revenueDataset = {
    type: "bar" as const,
    label: t("Dashboard::Cash in"),
    backgroundColor: monthlySummaries.map(() => "#5541F6"),
    borderSkipped: "top" as const,
    data: monthlySummaries.map((summary) => Number(summary.revenue)),
    fill: false,
    stack: 1,
  };

  const expectedRevenueDataset = {
    type: "bar" as const,
    label: t("Dashboard::Expected cash in"),
    backgroundColor: monthlySummaries.map(() => pattern.draw("diagonal", "white", "#5541F6", 3)),
    borderSkipped: "top" as const,
    data: monthlySummaries.map((summary) => Number(summary.expected_revenue)),
    fill: false,
    stack: 1,
  };

  const expenseDataset = {
    type: "bar" as const,
    label: t("Dashboard::Cash out"),
    backgroundColor: monthlySummaries.map(() => "#21A1FF"),
    borderSkipped: "top" as const,
    data: monthlySummaries.map((summary) => Number(summary.expenses)),
    fill: false,
    stack: 2,
  };

  const expectedExpenseDataset = {
    type: "bar" as const,
    label: t("Dashboard::Expected cash out"),
    backgroundColor: monthlySummaries.map(() => pattern.draw("diagonal", "white", "#21A1FF", 3)),
    borderSkipped: "top" as const,
    data: monthlySummaries.map((summary) => Number(summary.expected_expenses)),
    fill: false,
    stack: 2,
  };

  const netIncomeDataset = {
    type: "line" as const,
    label: t("Dashboard::Profit"),
    backgroundColor: monthlySummaries.map(() => "#F14A86"),
    data: monthlySummaries.map((summary) => Number(summary.net_income)),
    fill: false,
    borderColor: "#F14A86",
    stack: 3,
  };

  const expectedNetIncomeDataset = {
    type: "line" as const,
    label: t("Dashboard::Expected profit"),
    backgroundColor: monthlySummaries.map(() => "#F14A86"),
    data: monthlySummaries.map((summary) => Number(summary.expected_net_income)),
    fill: false,
    borderColor: "#F14A86",
    borderDash: [8, 5],
    stack: 3,
  };

  const placeholderNetincomeBarDataset = {
    type: "bar" as const,
    label: "",
    backgroundColor: monthlySummaries.map(() => "transparent"),
    data: monthlySummaries.map(() => 0),
    fill: false,
    stack: 3,
  };

  const dataPoints = [...revenueDataset.data, ...expenseDataset.data, ...netIncomeDataset.data];

  const maxDataValue = Math.max(...dataPoints);
  const minDataValue = Math.min(...dataPoints);
  const suggestedMaxValue = isFinite(maxDataValue) ? maxDataValue + (maxDataValue * 5) / 100 : 0;
  const suggestedMinValue = isFinite(minDataValue) ? minDataValue - (minDataValue * 5) / 100 : 0;

  const data = {
    labels: labels,
    datasets: [
      netIncomeDataset,
      expectedNetIncomeDataset,
      revenueDataset,
      expectedRevenueDataset,
      placeholderNetincomeBarDataset,
      expenseDataset,
      expectedExpenseDataset,
    ],
  };

  const hasData = dataPoints.some((dataPoint) => Math.abs(dataPoint) > 0);

  return (
    <div className="panel h-full flex flex-col mb-0!">
      <div className="panel-header is-large space-between">
        {t("Dashboard::Financial performance")}

        <div className="dashboard-financial-performance-legend">
          <div className="legend-item">
            <span className="legend-item-circle is-revenue"></span>
            {t("Dashboard::Cash in")}
          </div>

          <div className="legend-item">
            <span className="legend-item-circle is-expense"></span>
            {t("Dashboard::Cash out")}
          </div>

          <div className="legend-item">
            <span className="legend-item-circle is-net-total"></span>
            {`${t("Dashboard::Profit")} / ${t("Dashboard::Loss")}`}
          </div>
        </div>
      </div>

      <div className="panel-content is-large min-h-0 flex-1 overflow-auto">
        <GraphContainer className="h-full">
          <GraphWrapper>
            {!hasData && (
              <GraphEmpty
                title={t("Dashboard::No data available for current financial year.")}
                description={t("Dashboard::Come back later when you've added data.")}
              />
            )}
            <Bar
              data={data}
              options={{
                maintainAspectRatio: false,
                legend: {
                  display: false,
                },
                tooltips: {
                  mode: "point",
                  backgroundColor: "#212B36",
                  footerMarginTop: 100,
                  callbacks: {
                    label: (tooltipItem, data) => {
                      const label = data.datasets[tooltipItem.datasetIndex].label || "";
                      const value = NumberFormatter.formatCurrency(
                        currency,
                        numberFormat,
                        Math.abs(Number(tooltipItem.value))
                      );

                      if ([0, 1].includes(tooltipItem.datasetIndex) && Number(tooltipItem.value) < 0) {
                        if (tooltipItem.datasetIndex === 0) return `${t("Dashboard::Loss")}: ${value}`;
                        if (tooltipItem.datasetIndex === 1) return `${t("Dashboard::Expected loss")}: ${value}`;
                      }

                      return `${label}: ${value}`;
                    },
                  },
                },
                scales: {
                  xAxes: [
                    {
                      gridLines: {
                        color: "#F4F6F8",
                        lineWidth: 2,
                        borderDash: [8, 4],
                      },
                      ticks: {
                        fontSize: 13,
                        fontColor: "#637381",
                        padding: 10,
                        callback: function (value) {
                          return moment(value, "YYYY-MM-DD").format("MMM");
                        },
                      },
                    },
                  ],
                  yAxes: [
                    {
                      gridLines: {
                        drawBorder: false,
                        color: "#F4F6F8",
                        lineWidth: 2,
                        drawTicks: false,
                        zeroLineColor: "#DFE4E8",
                        zeroLineWidth: 2,
                      },
                      ticks: {
                        fontSize: 13,
                        fontColor: "#637381",
                        padding: 20,
                        suggestedMin: suggestedMinValue,
                        suggestedMax: suggestedMaxValue,
                        callback: (value) => {
                          return NumberFormatter.formatCurrency(currency, numberFormat, value);
                        },
                      },
                    },
                  ],
                },
              }}
            />
          </GraphWrapper>
        </GraphContainer>
      </div>
    </div>
  );
};

export default FinancialPerformanceWidget;
